import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import { Pie } from 'react-chartjs-2';
import { 
  faPercentage, 
  faCalculator, 
  faChartPie, 
  faInfoCircle, 
  faStar, 
  faLightbulb 
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const PercentOffCalculator = () => {
  const [originalPrice, setOriginalPrice] = useState('');
  const [discountedPrice, setDiscountedPrice] = useState('');
  const [percentOff, setPercentOff] = useState('');
  const [savings, setSavings] = useState('');
  const [error, setError] = useState('');
  const [chartData, setChartData] = useState(null);

  const calculatePercentOff = () => {
    try {
      const origPrice = parseFloat(originalPrice);
      const discPrice = parseFloat(discountedPrice);

      if (isNaN(origPrice) || origPrice <= 0) {
        throw new Error('Please enter a valid original price');
      }

      if (isNaN(discPrice) || discPrice <= 0) {
        throw new Error('Please enter a valid discounted price');
      }

      if (discPrice >= origPrice) {
        throw new Error('Discounted price must be less than original price');
      }

      const percentage = ((origPrice - discPrice) / origPrice) * 100;
      const saved = origPrice - discPrice;

      setPercentOff(percentage.toFixed(2));
      setSavings(saved.toFixed(2));
      setError('');

      // Update chart data
      setChartData({
        labels: ['Original Price', 'Discounted Price', 'Savings'],
        datasets: [{
          data: [origPrice, discPrice, saved],
          backgroundColor: [
            'rgba(255, 99, 132, 0.7)',
            'rgba(54, 162, 235, 0.7)',
            'rgba(75, 192, 192, 0.7)'
          ],
          hoverOffset: 4
        }]
      });
    } catch (err) {
      setError(err.message);
      setPercentOff('');
      setSavings('');
      setChartData(null);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-100 to-indigo-100">
      <Helmet>
        <title>Percent Off Calculator</title>
        <meta name="description" content="Calculate percentage off and savings with our discount calculator. Get instant results and visualize your savings." />
      </Helmet>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <FontAwesomeIcon 
            icon={faCalculator} 
            className="text-4xl text-blue-600 mb-4" 
          />
          <h1 className="text-4xl font-bold text-gray-800 mb-2">Percent Off Calculator</h1>
          <p className="text-xl text-gray-600">Calculate percentage off and savings instantly</p>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Original Price</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={originalPrice}
                    onChange={(e) => setOriginalPrice(e.target.value)}
                    className="flex-1 rounded-r-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter original price"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Discounted Price</label>
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500">
                    ₹
                  </span>
                  <input
                    type="number"
                    value={discountedPrice}
                    onChange={(e) => setDiscountedPrice(e.target.value)}
                    className="flex-1 rounded-r-md border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    placeholder="Enter discounted price"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={calculatePercentOff}
                className="w-full bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Calculate Percent Off
              </button>
            </div>

            {error && (
              <div className="mt-4 p-4 bg-red-100 text-red-700 rounded-md">
                {error}
              </div>
            )}

            {percentOff && (
              <div className="mt-8">
                <h2 className="text-xl font-semibold mb-4 text-blue-600">Results</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faPercentage} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Percent Off</h3>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{percentOff}%</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faCalculator} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Savings</h3>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">₹{savings}</p>
                  </div>

                  <div className="bg-gray-50 p-6 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FontAwesomeIcon icon={faStar} className="text-blue-600 mr-3" />
                      <h3 className="text-lg font-semibold text-gray-700">Total Savings</h3>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">₹{savings}</p>
                  </div>
                </div>

                <div className="mt-8">
                  <h2 className="text-xl font-semibold mb-4 text-blue-600">Solution Explanation</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Original Price</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">Starting price: ₹{originalPrice}</p>
                        <p className="text-gray-500">This is the initial price before any discount.</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Discounted Price</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">Final price: ₹{discountedPrice}</p>
                        <p className="text-gray-500">This is the price after discount.</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Percent Off Calculation</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">Formula: ((Original Price - Discounted Price) ÷ Original Price) × 100</p>
                        <p className="text-gray-500">Calculation: ((₹{originalPrice} - ₹{discountedPrice}) ÷ ₹{originalPrice}) × 100 = {percentOff}%</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Savings</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">Formula: Original Price - Discounted Price</p>
                        <p className="text-gray-500">Calculation: ₹{originalPrice} - ₹{discountedPrice} = ₹{savings}</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Key Concepts</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">• Percent Off: Shows discount as a percentage</p>
                        <p className="text-gray-600">• Savings: Actual money saved</p>
                        <p className="text-gray-600">• Discounted Price: Final price after discount</p>
                      </div>
                    </div>

                    <div className="bg-white p-6 rounded-lg shadow-sm">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Quick Tips</h3>
                      <div className="space-y-2">
                        <p className="text-gray-600">• Higher discount means higher percent off</p>
                        <p className="text-gray-600">• Savings increase with higher original price</p>
                        <p className="text-gray-600">• Percent off can't be more than 100%</p>
                      </div>
                    </div>
                  </div>

                  {chartData && (
                    <div className="mt-8">
                      <h2 className="text-xl font-semibold mb-4 text-blue-600">Price Breakdown</h2>
                      <div className="bg-white p-6 rounded-lg shadow-md">
                        <div className="bg-gray-50 p-4 rounded-lg mb-4">
                          <p className="text-sm text-gray-600">This pie chart shows the breakdown of your prices and savings.</p>
                        </div>
                        <div className="relative h-[300px]">
                          <Pie
                            data={chartData}
                            options={{
                              responsive: true,
                              maintainAspectRatio: false,
                              plugins: {
                                legend: {
                                  position: 'top',
                                },
                                title: {
                                  display: true,
                                  text: 'Price Distribution'
                                }
                              }
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="mt-12">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">Features & Benefits</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center">
                <FontAwesomeIcon icon={faLightbulb} className="text-yellow-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Instant Calculations</h3>
                  <p className="text-gray-600">Get real-time percent off calculations as you type</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faChartPie} className="text-blue-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Visual Breakdown</h3>
                  <p className="text-gray-600">See exactly how your prices and savings are distributed</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faInfoCircle} className="text-green-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">Savings Tracker</h3>
                  <p className="text-gray-600">Track your savings with each discount</p>
                </div>
              </div>
              <div className="flex items-center">
                <FontAwesomeIcon icon={faStar} className="text-purple-500 mr-4" />
                <div>
                  <h3 className="font-semibold text-gray-700">User-friendly Interface</h3>
                  <p className="text-gray-600">Clean and intuitive design for easy calculations</p>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">How to Use</h2>
            <div className="space-y-4">
              <div>
                <h3 className="font-semibold text-gray-700">Step 1: Enter Prices</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Input the original price of the item</li>
                  <li>Input the discounted price of the item</li>
                  <li>Use the ₹ prefix for clarity</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Step 2: Calculate Percent Off</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Click the "Calculate Percent Off" button</li>
                  <li>View the percentage off and savings</li>
                  <li>Check your total savings</li>
                </ol>
              </div>

              <div>
                <h3 className="font-semibold text-gray-700">Step 3: Analyze Results</h3>
                <ol className="list-decimal pl-6 mt-2 space-y-2">
                  <li>Review the percent off calculation</li>
                  <li>Check the savings amount</li>
                  <li>Analyze the pie chart for visual breakdown</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="mt-8 bg-white rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4 text-blue-600">FAQ</h2>
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">What is percent off?</h3>
                <p className="text-gray-600">Percent off is the percentage reduction from the original price to the discounted price.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">How is percent off calculated?</h3>
                <p className="text-gray-600">Percent off is calculated using the formula: ((Original Price - Discounted Price) ÷ Original Price) × 100</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">What does the pie chart show?</h3>
                <p className="text-gray-600">The pie chart shows the distribution of your original price, discounted price, and savings.</p>
              </div>

              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-700 mb-2">Can I use this for multiple items?</h3>
                <p className="text-gray-600">Yes! You can use this calculator for any item. Just enter the original price and discounted price for each item separately.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PercentOffCalculator;
